import { assertNever } from "@/shared/lib/assertNever";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseKeyValue } from "@/shared/ui/BaseKeyValue";
import { BaseLink } from "@/shared/ui/base-link";
import { DeliveryRoute } from "@/widgets/delivery-route/ui";
import { Box, Collapse, Stack } from "@mui/material";
import type { FreightMatching } from "@x-mile/api-contract";
import { useState } from "react";

export type OfferCargoDestinationsInfoProps =
  | {
      variant: "shipper_cargo_detail";
      draftOfferCargo: FreightMatching.schemas.OfferCargo;
    }
  | {
      variant: "shipper_delivery_proposal_detail";
      draftOfferCargo: FreightMatching.schemas.OfferCargo;
      deliveryProposalOfferCargo: FreightMatching.schemas.OfferCargo;
    }
  | {
      variant: "shipper_contract_detail";
      draftOfferCargo: FreightMatching.schemas.OfferCargo;
      contractOfferCargo: FreightMatching.schemas.OfferCargo;
    }
  | {
      variant: "carrier_cargo_detail";
      draftOfferCargo: FreightMatching.schemas.OfferCargo;
      deliveryProposalOfferCargo:
        | FreightMatching.schemas.OfferCargo
        | undefined;
    }
  | {
      variant: "carrier_contract_detail";
      draftOfferCargo: FreightMatching.schemas.OfferCargo;
      deliveryProposalOfferCargo: FreightMatching.schemas.OfferCargo;
      contractOfferCargo: FreightMatching.schemas.OfferCargo;
    };

export const OfferCargoDestinationsInfo = (
  props: OfferCargoDestinationsInfoProps,
) => {
  const [fullShow, setFullShow] = useState(false);

  const baseOfferCargo = (() => {
    switch (props.variant) {
      case "shipper_cargo_detail":
        return props.draftOfferCargo;
      case "shipper_delivery_proposal_detail":
        return props.deliveryProposalOfferCargo;
      case "shipper_contract_detail":
        return props.contractOfferCargo;
      case "carrier_cargo_detail":
        if (props.deliveryProposalOfferCargo) {
          return props.deliveryProposalOfferCargo;
        }
        return props.draftOfferCargo;
      case "carrier_contract_detail":
        return props.deliveryProposalOfferCargo;
      default:
        assertNever(props);
        throw new Error(`Unexpected variant: ${props}`);
    }
  })();
  const diffOfferCargo = (() => {
    switch (props.variant) {
      case "shipper_cargo_detail":
        return undefined;
      case "shipper_delivery_proposal_detail":
        return props.draftOfferCargo;
      case "shipper_contract_detail":
        return undefined;
      case "carrier_cargo_detail":
        if (props.deliveryProposalOfferCargo) {
          return props.draftOfferCargo;
        }
        return undefined;
      case "carrier_contract_detail":
        return undefined;
      default:
        assertNever(props);
        throw new Error(`Unexpected variant: ${props}`);
    }
  })();

  return (
    <Box>
      <Stack spacing={5}>
        <Box
          position="relative"
          maxWidth="628px"
          padding="24px 16px"
          border="1px solid"
          borderColor="grey.200"
          borderRadius="16px"
        >
          <Collapse in={fullShow} collapsedSize={200}>
            <DeliveryRoute
              baseOfferCargo={baseOfferCargo}
              diffOfferCargo={diffOfferCargo}
            />
          </Collapse>
          <BaseButton
            variant="outlined"
            onClick={() => setFullShow((value) => !value)}
            sx={{
              position: "absolute",
              left: "50%",
              bottom: "-18px",
              transform: "translateX(-50%)",
              bgcolor: "white",
            }}
            endIcon={
              <BaseIcon
                name="chevronUp"
                htmlColor="#52A48B"
                sx={{
                  fontWeight: 700,
                  transition: "0.3s",
                  transform: fullShow ? " rotate(0deg)" : "rotate(180deg)",
                }}
              />
            }
          >
            {fullShow ? "閉じる" : "すべて表示"}
          </BaseButton>
        </Box>
        {props.draftOfferCargo.attachedDestinationFiles && (
          <BaseKeyValue keyLabel="添付ファイル">
            <Stack>
              {props.draftOfferCargo.attachedDestinationFiles.map((file) => (
                <BaseLink href={file.url} key={file.url}>
                  {file.filename}
                </BaseLink>
              ))}
            </Stack>
          </BaseKeyValue>
        )}
      </Stack>
    </Box>
  );
};
